// Gopher API Service for Twitter News and Sentiment Analysis
export interface TwitterTweet {
  id: string;
  text: string;
  author: {
    username: string;
    name: string;
    verified: boolean;
    followers_count: number;
  };
  created_at: string;
  public_metrics: {
    retweet_count: number;
    like_count: number;
    reply_count: number;
    quote_count: number;
  };
  sentiment?: 'bullish' | 'bearish' | 'neutral';
  relevance_score?: number;
}

export interface TwitterSearchResponse {
  data: TwitterTweet[];
  meta: {
    result_count: number;
    newest_id: string;
    oldest_id: string;
  };
}

export interface SentimentAnalysis {
  overall_sentiment: 'bullish' | 'bearish' | 'neutral';
  sentiment_score: number; // -1 to 1 (bearish to bullish)
  confidence: number; // 0 to 1
  bullish_tweets: number;
  bearish_tweets: number;
  neutral_tweets: number;
  total_tweets: number;
  key_themes: string[];
  influential_tweets: TwitterTweet[];
  summary: string;
}

export interface TokenNewsData {
  token: string;
  tweets: TwitterTweet[];
  sentiment: SentimentAnalysis;
  last_updated: string;
}

class GopherService {
  private apiKey: string;
  private baseUrl = 'https://data.gopher-ai.com/api/v1';

  constructor() {
    this.apiKey = import.meta.env.VITE_GOPHER_API_KEY || '';
    if (!this.apiKey) {
      console.warn('Gopher API key not found. Twitter news features will be disabled.');
    }
  }

  private async makeRequest(endpoint: string, data: any): Promise<any> {
    if (!this.apiKey) {
      throw new Error('Gopher API key not configured');
    }

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Gopher API error: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Gopher API request failed:', error);
      throw error;
    }
  }

  async searchTwitterByToken(token: string, maxResults: number = 20): Promise<TwitterTweet[]> {
    try {
      // Create comprehensive search query for the token
      const queries = [
        `$${token.toUpperCase()}`,
        `#${token.toUpperCase()}`,
        `${token.toUpperCase()} token`,
        `${token.toUpperCase()} crypto`,
        `${token.toUpperCase()} price`
      ];

      const searchQuery = queries.join(' OR ');

      const requestData = {
        type: 'twitter',
        arguments: {
          type: 'searchbyquery',
          query: searchQuery,
          max_results: maxResults,
          tweet_fields: 'created_at,author_id,public_metrics,context_annotations,entities',
          user_fields: 'username,name,verified,public_metrics',
          expansions: 'author_id'
        }
      };

      console.log(`🔍 Searching Twitter for token: ${token}`);
      const response = await this.makeRequest('/search/live/twitter', requestData);

      // Process and format the response
      const tweets: TwitterTweet[] = this.processTweets(response.data || []);
      
      console.log(`📊 Found ${tweets.length} tweets for ${token}`);
      return tweets;

    } catch (error) {
      console.error(`Failed to search Twitter for token ${token}:`, error);
      return [];
    }
  }

  private processTweets(rawTweets: any[]): TwitterTweet[] {
    return rawTweets.map(tweet => ({
      id: tweet.id,
      text: tweet.text,
      author: {
        username: tweet.author?.username || 'unknown',
        name: tweet.author?.name || 'Unknown User',
        verified: tweet.author?.verified || false,
        followers_count: tweet.author?.public_metrics?.followers_count || 0
      },
      created_at: tweet.created_at,
      public_metrics: {
        retweet_count: tweet.public_metrics?.retweet_count || 0,
        like_count: tweet.public_metrics?.like_count || 0,
        reply_count: tweet.public_metrics?.reply_count || 0,
        quote_count: tweet.public_metrics?.quote_count || 0
      },
      sentiment: this.analyzeTweetSentiment(tweet.text),
      relevance_score: this.calculateRelevanceScore(tweet)
    }));
  }

  private analyzeTweetSentiment(text: string): 'bullish' | 'bearish' | 'neutral' {
    const bullishKeywords = [
      'moon', 'bullish', 'pump', 'rocket', 'gem', 'buy', 'hodl', 'diamond hands',
      'to the moon', 'ath', 'breakout', 'rally', 'surge', 'gains', 'profit',
      'long', 'accumulate', 'dip buying', 'strong', 'support', 'bounce'
    ];

    const bearishKeywords = [
      'dump', 'crash', 'bearish', 'sell', 'short', 'drop', 'fall', 'decline',
      'resistance', 'breakdown', 'correction', 'dip', 'loss', 'red', 'bleeding',
      'panic', 'fear', 'weak', 'capitulation', 'bottom', 'oversold'
    ];

    const lowerText = text.toLowerCase();
    
    let bullishScore = 0;
    let bearishScore = 0;

    bullishKeywords.forEach(keyword => {
      if (lowerText.includes(keyword)) bullishScore++;
    });

    bearishKeywords.forEach(keyword => {
      if (lowerText.includes(keyword)) bearishScore++;
    });

    if (bullishScore > bearishScore) return 'bullish';
    if (bearishScore > bullishScore) return 'bearish';
    return 'neutral';
  }

  private calculateRelevanceScore(tweet: any): number {
    let score = 0.5; // Base score

    // Boost for verified users
    if (tweet.author?.verified) score += 0.2;

    // Boost for high follower count
    const followers = tweet.author?.public_metrics?.followers_count || 0;
    if (followers > 100000) score += 0.2;
    else if (followers > 10000) score += 0.1;

    // Boost for engagement
    const engagement = (tweet.public_metrics?.like_count || 0) + 
                     (tweet.public_metrics?.retweet_count || 0) + 
                     (tweet.public_metrics?.reply_count || 0);
    
    if (engagement > 100) score += 0.2;
    else if (engagement > 10) score += 0.1;

    return Math.min(score, 1.0);
  }

  async analyzeSentiment(tweets: TwitterTweet[]): Promise<SentimentAnalysis> {
    if (tweets.length === 0) {
      return {
        overall_sentiment: 'neutral',
        sentiment_score: 0,
        confidence: 0,
        bullish_tweets: 0,
        bearish_tweets: 0,
        neutral_tweets: 0,
        total_tweets: 0,
        key_themes: [],
        influential_tweets: [],
        summary: 'No tweets found for analysis.'
      };
    }

    const bullishTweets = tweets.filter(t => t.sentiment === 'bullish');
    const bearishTweets = tweets.filter(t => t.sentiment === 'bearish');
    const neutralTweets = tweets.filter(t => t.sentiment === 'neutral');

    // Calculate weighted sentiment score
    let sentimentScore = 0;
    let totalWeight = 0;

    tweets.forEach(tweet => {
      const weight = (tweet.relevance_score || 0.5) * 
                    (1 + Math.log10((tweet.author.followers_count || 1) + 1) / 10);
      
      if (tweet.sentiment === 'bullish') sentimentScore += weight;
      else if (tweet.sentiment === 'bearish') sentimentScore -= weight;
      
      totalWeight += weight;
    });

    const normalizedScore = totalWeight > 0 ? sentimentScore / totalWeight : 0;
    
    // Determine overall sentiment
    let overallSentiment: 'bullish' | 'bearish' | 'neutral' = 'neutral';
    if (normalizedScore > 0.2) overallSentiment = 'bullish';
    else if (normalizedScore < -0.2) overallSentiment = 'bearish';

    // Extract key themes
    const keyThemes = this.extractKeyThemes(tweets);

    // Get most influential tweets
    const influentialTweets = tweets
      .sort((a, b) => (b.relevance_score || 0) - (a.relevance_score || 0))
      .slice(0, 5);

    // Generate summary
    const summary = this.generateSentimentSummary({
      overall_sentiment: overallSentiment,
      sentiment_score: normalizedScore,
      bullish_tweets: bullishTweets.length,
      bearish_tweets: bearishTweets.length,
      neutral_tweets: neutralTweets.length,
      total_tweets: tweets.length,
      key_themes: keyThemes
    });

    return {
      overall_sentiment: overallSentiment,
      sentiment_score: normalizedScore,
      confidence: Math.min(tweets.length / 20, 1.0), // Higher confidence with more tweets
      bullish_tweets: bullishTweets.length,
      bearish_tweets: bearishTweets.length,
      neutral_tweets: neutralTweets.length,
      total_tweets: tweets.length,
      key_themes: keyThemes,
      influential_tweets: influentialTweets,
      summary
    };
  }

  private extractKeyThemes(tweets: TwitterTweet[]): string[] {
    const themes = new Map<string, number>();
    
    const commonThemes = [
      'price action', 'technical analysis', 'fundamentals', 'adoption',
      'partnerships', 'development', 'community', 'trading volume',
      'market cap', 'liquidity', 'staking', 'yield farming', 'defi',
      'nft', 'gaming', 'metaverse', 'regulation', 'institutional'
    ];

    tweets.forEach(tweet => {
      const text = tweet.text.toLowerCase();
      commonThemes.forEach(theme => {
        if (text.includes(theme.replace(' ', '')) || text.includes(theme)) {
          themes.set(theme, (themes.get(theme) || 0) + 1);
        }
      });
    });

    return Array.from(themes.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([theme]) => theme);
  }

  private generateSentimentSummary(analysis: Partial<SentimentAnalysis>): string {
    const { overall_sentiment, bullish_tweets, bearish_tweets, total_tweets, key_themes } = analysis;
    
    let summary = `Based on ${total_tweets} recent tweets, the overall sentiment is ${overall_sentiment?.toUpperCase()}. `;
    
    if (bullish_tweets && bearish_tweets) {
      const bullishPercent = Math.round((bullish_tweets / (total_tweets || 1)) * 100);
      const bearishPercent = Math.round((bearish_tweets / (total_tweets || 1)) * 100);
      
      summary += `${bullishPercent}% of tweets are bullish, ${bearishPercent}% are bearish. `;
    }

    if (key_themes && key_themes.length > 0) {
      summary += `Key discussion themes include: ${key_themes.slice(0, 3).join(', ')}.`;
    }

    return summary;
  }

  async getTokenNews(token: string): Promise<TokenNewsData> {
    try {
      console.log(`📰 Fetching news for token: ${token}`);
      
      const tweets = await this.searchTwitterByToken(token, 25);
      const sentiment = await this.analyzeSentiment(tweets);

      return {
        token: token.toUpperCase(),
        tweets,
        sentiment,
        last_updated: new Date().toISOString()
      };

    } catch (error) {
      console.error(`Failed to get token news for ${token}:`, error);
      
      // Return empty data structure on error
      return {
        token: token.toUpperCase(),
        tweets: [],
        sentiment: {
          overall_sentiment: 'neutral',
          sentiment_score: 0,
          confidence: 0,
          bullish_tweets: 0,
          bearish_tweets: 0,
          neutral_tweets: 0,
          total_tweets: 0,
          key_themes: [],
          influential_tweets: [],
          summary: 'Unable to fetch news data at this time.'
        },
        last_updated: new Date().toISOString()
      };
    }
  }

  // Demo mode for when API key is not available
  async getDemoTokenNews(token: string): Promise<TokenNewsData> {
    const demoTweets: TwitterTweet[] = [
      {
        id: '1',
        text: `$${token.toUpperCase()} looking bullish! Strong support at current levels 🚀`,
        author: { username: 'cryptotrader', name: 'Crypto Trader', verified: true, followers_count: 50000 },
        created_at: new Date(Date.now() - 3600000).toISOString(),
        public_metrics: { retweet_count: 25, like_count: 150, reply_count: 12, quote_count: 5 },
        sentiment: 'bullish',
        relevance_score: 0.8
      },
      {
        id: '2',
        text: `Technical analysis on $${token.toUpperCase()}: Breaking resistance, next target $2.50`,
        author: { username: 'techanalyst', name: 'TA Expert', verified: false, followers_count: 15000 },
        created_at: new Date(Date.now() - 7200000).toISOString(),
        public_metrics: { retweet_count: 45, like_count: 200, reply_count: 30, quote_count: 8 },
        sentiment: 'bullish',
        relevance_score: 0.9
      },
      {
        id: '3',
        text: `Caution on $${token.toUpperCase()} - seeing some selling pressure at these levels`,
        author: { username: 'riskmanager', name: 'Risk Manager', verified: false, followers_count: 8000 },
        created_at: new Date(Date.now() - 10800000).toISOString(),
        public_metrics: { retweet_count: 12, like_count: 80, reply_count: 15, quote_count: 3 },
        sentiment: 'bearish',
        relevance_score: 0.6
      }
    ];

    const sentiment = await this.analyzeSentiment(demoTweets);

    return {
      token: token.toUpperCase(),
      tweets: demoTweets,
      sentiment,
      last_updated: new Date().toISOString()
    };
  }

  isConfigured(): boolean {
    return !!this.apiKey;
  }
}

export const gopherService = new GopherService();
export default gopherService;
